import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowRight, Instagram, Check, Star, Plus } from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import InstagramFeed from "@/components/site/InstagramFeed";
import FaqSection from "@/components/site/FaqSection";
import { Icon, Eyebrow } from "@/components/site/ui";
import { SERVICES, PACKAGES, GUARANTEES, STATS, CONTACT, waLink, IMAGES } from "@/data/site";
import logo from "@/assets/brand/logo.jpg";
import garanti from "@/assets/brand/garanti.jpg";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yStatue = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div>
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <motion.div style={{ y: yGlow }} className="absolute -top-20 -left-20 w-[520px] h-[520px] bg-cobalt/25 blur-[150px] rounded-full" />
        <motion.div style={{ y: yGlow }} className="absolute top-40 right-0 w-[480px] h-[480px] bg-neon-magenta/25 blur-[150px] rounded-full" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(217,70,239,0.12),transparent_60%)]" />

        <motion.img
          style={{ y: yStatue, opacity }}
          src={IMAGES.statuePortrait}
          alt=""
          className="pointer-events-none absolute right-0 bottom-0 h-[70%] sm:h-[85%] w-auto object-contain opacity-40 lg:opacity-70 radial-fade mix-blend-luminosity"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full pt-10 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>İzmir · Sosyal Medya &amp; Prodüksiyon Ajansı</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-display font-extrabold tracking-tight text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
            <MaskedLines lines={["Markanı", "büyüten"]} delay={0.15} />
            <span className="mask-line">
              <motion.span
                className="block text-gradient"
                initial={{ y: "110%" }} animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.39 }}
              >
                dijital güç.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 max-w-xl text-lg sm:text-xl text-slate-300 leading-relaxed"
          >
            Bot yok, tamamen <span className="text-white font-semibold">organik büyüme.</span> Profesyonel çekim, içerik stratejisi ve Meta reklam yönetimiyle Ege'nin en güçlü sosyal medya ajansı.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink(CONTACT.phoneRaw, "Merhaba İzmir Medya, sosyal medya paketleriniz hakkında bilgi almak istiyorum.")}
              target="_blank" rel="noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-cobalt to-neon-magenta text-white font-semibold text-lg glow-magenta hover:scale-[1.03] transition-transform"
            >
              <MessageCircle className="w-5 h-5" /> Ücretsiz Teklif Al
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/paketler"
              data-testid="hero-packages-link"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full glass border border-white/15 text-white font-semibold text-lg hover:bg-white/10 transition-colors"
            >
              Paketleri İncele
            </Link>
          </motion.div>

          {/* stats */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                <div className="font-display text-3xl sm:text-4xl font-extrabold text-gradient">{s.value}</div>
                <div className="text-xs sm:text-sm text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <section className="border-y border-white/10 bg-ink-900/60">
        <Marquee items={["SOSYAL MEDYA YÖNETİMİ", "PRODÜKSİYON", "DRONE ÇEKİM", "REELS KURGU", "META REKLAM", "DİJİTAL STRATEJİ"]} />
      </section>

      {/* ===== GUARANTEE ===== */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <Eyebrow>100'de 100 Büyüme Garantisi</Eyebrow>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold leading-tight">
                Sözde değil,<br /><span className="text-gradient">sonuçta iddialıyız.</span>
              </h2>
              <p className="mt-5 text-slate-300 text-lg max-w-lg">
                Her paket, ölçülebilir büyüme taahhüdüyle gelir. Gerçek takipçi, gerçek etkileşim, gerçek satış.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {GUARANTEES.map((g, i) => (
                  <Reveal key={g.title} delay={i * 0.05}>
                    <div className="flex gap-3 p-4 rounded-2xl glass border border-white/10 h-full">
                      <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-cobalt/30 to-neon-magenta/30 border border-white/10">
                        <Icon name={g.icon} className="w-5 h-5 text-neon-pink" />
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{g.title}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{g.desc}</div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15} className="relative">
              <div className="absolute -inset-6 bg-cobalt/20 blur-[80px] rounded-full" />
              <div className="relative neon-border rounded-3xl overflow-hidden">
                <img src={garanti} alt="%100 Büyüme Garantisi" className="w-full h-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== SERVICES BENTO ===== */}
      <section className="relative py-20 sm:py-28 bg-ink-900/50 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <Reveal>
              <Eyebrow>Hizmetler</Eyebrow>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold">Uçtan uca dijital prodüksiyon</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/hizmetler" className="inline-flex items-center gap-2 text-slate-300 hover:text-white group">
                Tüm hizmetler <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <Stagger className="grid md:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <StaggerItem key={s.id}>
                <div data-testid={`service-card-${s.id}`} className="group relative h-full p-7 rounded-3xl bg-ink-700 border border-white/10 hover:border-neon-magenta/50 transition-colors overflow-hidden">
                  <div className="absolute -right-10 -top-10 w-32 h-32 bg-neon-magenta/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-gradient-to-br from-cobalt to-neon-purple">
                    <Icon name={s.icon} className="w-6 h-6 text-white" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  <ul className="mt-4 space-y-1.5">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-xs text-slate-300">
                        <Check className="w-3.5 h-3.5 text-neon-pink shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ===== PACKAGES PREVIEW ===== */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Eyebrow className="justify-center">Kampanyalı Paketler</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold">Sana uygun paketi seç</h2>
            <p className="mt-4 text-slate-300">Kampanya ay sonuna kadar geçerli. Fiyatlar sınırlı süre için indirimlidir.</p>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-3 gap-6 items-stretch">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <div
                  data-testid={`home-package-${p.id}`}
                  className={`relative h-full flex flex-col rounded-3xl p-8 border transition-transform hover:-translate-y-1 ${
                    p.highlight ? "neon-border glass-strong glow-magenta lg:-mt-4 lg:mb-4" : "bg-ink-700 border-white/10"
                  }`}
                >
                  {p.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-4 py-1 rounded-full bg-gradient-to-r from-cobalt to-neon-magenta text-white text-xs font-bold">
                      <Star className="w-3 h-3 fill-white" /> EN POPÜLER
                    </span>
                  )}
                  <div className="font-mono text-xs uppercase tracking-widest text-cobalt-300">{p.subtitle}</div>
                  <h3 className="mt-2 font-display text-3xl font-extrabold">{p.name}</h3>
                  <div className="mt-5 flex items-end gap-2">
                    <span className="text-slate-500 line-through text-lg">{p.oldPrice}₺</span>
                    <span className="font-display text-4xl font-extrabold text-gradient">{p.price}₺</span>
                    <span className="text-slate-400 text-sm mb-1">/ay</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.slice(0, 6).map((f) => (
                      <li key={f} className="flex gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-neon-pink shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                    {p.features.length > 6 && (
                      <li className="flex gap-2 text-sm text-slate-400">
                        <Plus className="w-4 h-4 text-cobalt-300 shrink-0 mt-0.5" /> +{p.features.length - 6} özellik daha
                      </li>
                    )}
                  </ul>
                  <a
                    href={waLink(CONTACT.phoneRaw, `Merhaba, ${p.name} paketi (${p.price}₺) hakkında bilgi almak istiyorum.`)}
                    target="_blank" rel="noreferrer"
                    data-testid={`home-package-cta-${p.id}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-transform hover:scale-[1.03] ${
                      p.highlight ? "bg-gradient-to-r from-cobalt to-neon-magenta text-white" : "bg-white/10 text-white hover:bg-white/15"
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" /> Bu Paketi Seç
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MANIFESTO / WHY ===== */}
      <section className="relative py-20 sm:py-28 bg-ink-900/50 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.camera} alt="Profesyonel çekim" className="rounded-2xl h-full object-cover aspect-[3/4] neon-border" />
              <img src={IMAGES.drone} alt="Drone çekim" className="rounded-2xl h-full object-cover aspect-[3/4] mt-8 neon-border" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow>Neden İzmir Medya?</Eyebrow>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold">Rakamlar değil,<br /><span className="text-gradient">sonuçlar konuşur.</span></h2>
            </Reveal>
            <div className="mt-8 space-y-6">
              {[
                { no: "01", t: "Tamamen Organik", d: "Sahte takipçi ve bot kullanmıyoruz. Gerçek kitle, gerçek etkileşim." },
                { no: "02", t: "Sinema Kalitesinde Prodüksiyon", d: "4K kameralar, drone çekimleri ve profesyonel kurgu ekibi." },
                { no: "03", t: "Veriyle Yönetim", d: "Her ay detaylı performans raporu ve strateji optimizasyonu." },
              ].map((c, i) => (
                <Reveal key={c.no} delay={i * 0.08}>
                  <div className="flex gap-5 pb-6 border-b border-white/10">
                    <span className="font-display text-2xl font-extrabold text-transparent [-webkit-text-stroke:1.5px_#D946EF]">{c.no}</span>
                    <div>
                      <h3 className="font-bold text-lg">{c.t}</h3>
                      <p className="text-slate-400 mt-1 text-sm">{c.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== INSTAGRAM FEED ===== */}
      <InstagramFeed />

      {/* ===== FAQ ===== */}
      <FaqSection />

      {/* ===== FINAL CTA ===== */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,82,255,0.15),transparent_65%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <img src={logo} alt="İzmir Medya" className="h-20 w-20 rounded-full mx-auto ring-1 ring-white/20 mb-8" />
            <h2 className="font-display text-4xl sm:text-6xl font-extrabold leading-tight">
              Markanı büyütmeye <span className="text-gradient">bugün başla.</span>
            </h2>
            <p className="mt-5 text-slate-300 text-lg max-w-xl mx-auto">
              Ücretsiz keşif görüşmesi için hemen yaz. Sosyal medyada bir sonraki fenomen sen ol.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a
                href={waLink(CONTACT.phoneRaw, "Merhaba İzmir Medya, ücretsiz keşif görüşmesi istiyorum.")}
                target="_blank" rel="noreferrer"
                data-testid="final-cta-whatsapp"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-cobalt to-neon-magenta text-white font-semibold text-lg glow-magenta hover:scale-[1.03] transition-transform"
              >
                <MessageCircle className="w-5 h-5" /> {CONTACT.phone}
              </a>
              <a
                href={CONTACT.instagramUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full glass border border-white/15 text-white font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                <Instagram className="w-5 h-5" /> {CONTACT.instagram}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
